import { STORAGE } from "../storage";
import { removeSocket } from "../socket/socket";
import { initSoketiService } from "../soketi";
import { showToastMessage } from "../toastMessage";

/**
 * Migration Service for seamless Socket.IO to Soketi transition
 * 
 * This service handles the automatic migration from Socket.IO to Soketi
 * when soketiUrl becomes available in the backend response.
 * 
 * The migration process:
 * 1. Store the soketiUrl in local storage
 * 2. Gracefully disconnect Socket.IO
 * 3. Initialize Soketi service
 * 4. Process the config message through Soketi flow
 * 5. Handle migration failures with fallback to Socket.IO
 */

/**
 * Trigger migration from Socket.IO to Soketi
 * 
 * @param {Object} socket - Current Socket.IO instance
 * @param {string} soketiUrl - Soketi URL from backend response
 * @param {Object} configMessage - Original config message to process after migration
 * @returns {Promise<boolean>} - True if migration successful, false if failed
 */
export const triggerMigrationToSoketi = async (socket, soketiUrl, configMessage) => {
    try {
        console.log('MIGRATION - Starting seamless migration from Socket.IO to Soketi');
        
        // Show user-friendly message about service upgrade
        showToastMessage('Upgrading connection service...', 3000);
        
        // Step 1: Store Soketi URL for future use
        await STORAGE.set('soketiUrl', soketiUrl);
        console.log('MIGRATION - Soketi URL stored:', soketiUrl);
        
        // Step 2: Gracefully disconnect Socket.IO
        console.log('MIGRATION - Disconnecting Socket.IO service');
        removeSocket();
        
        // Step 3: Initialize Soketi service
        console.log('MIGRATION - Initializing Soketi service');
        const soketiInitSuccess = await initSoketiService();
        
        if (!soketiInitSuccess) {
            console.error('MIGRATION - Soketi initialization failed');
            throw new Error('Soketi initialization failed');
        }
        
        // Step 4: Process the config message through Soketi flow
        // The config will be processed by Soketi's onConnected handler
        // which will fetch the config via API and handle it exactly like Socket.IO
        console.log('MIGRATION - Soketi initialized successfully, config will be processed by Soketi');
        
        // Step 5: Show success message
        showToastMessage('Connection upgraded successfully', 2000);
        
        console.log('MIGRATION - Migration completed successfully');
        return true;
        
    } catch (error) {
        console.error('MIGRATION - Migration failed:', error);
        
        // Fallback: Remove soketiUrl and attempt to reconnect Socket.IO
        await handleMigrationFailure(socket);
        
        return false;
    }
};

/**
 * Handle migration failure by retrying Soketi connection
 *
 * IMPORTANT: We NEVER remove soketiUrl or fallback to Socket.IO once soketiUrl exists.
 * This ensures clients never downgrade to deprecated Socket.IO service.
 *
 * @param {Object} originalSocket - Original Socket.IO instance
 */
const handleMigrationFailure = async (originalSocket) => {
    try {
        // NEVER remove soketiUrl - we want to keep trying Soketi
        // await STORAGE.remove('soketiUrl'); // ← REMOVED: This was causing fallback to Socket.IO

        // Show user message about retry
        showToastMessage('Connection upgrade failed, will retry automatically', 3000);

        // Gracefully disconnect Socket.IO since we're committed to Soketi
        console.log('MIGRATION - Disconnecting Socket.IO, will rely on Soketi reconnection');
        if (originalSocket) {
            originalSocket.removeAllListeners();
            originalSocket.disconnect();
            originalSocket.close();
        }

        console.log('MIGRATION - Migration failure handled, Soketi will retry connection automatically');

    } catch (fallbackError) {
        console.error('MIGRATION - Migration failure handling failed:', fallbackError);
        showToastMessage('Connection error occurred, will retry automatically', 5000);
    }
};

/**
 * Check if migration is in progress
 * This can be used by other services to avoid conflicts during migration
 * 
 * @returns {boolean} - True if migration is currently in progress
 */
let migrationInProgress = false;

export const isMigrationInProgress = () => {
    return migrationInProgress;
};

/**
 * Set migration progress state
 * Internal function to track migration state
 * 
 * @param {boolean} inProgress - Migration progress state
 */
const setMigrationProgress = (inProgress) => {
    migrationInProgress = inProgress;
};

export default {
    triggerMigrationToSoketi,
    isMigrationInProgress
};
